import { onIdTokenChanged, type User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import type { OrgClaims, OrgId, Role } from '../types';
import { auth, db } from './firebase';

/**
 * Keeps the signed-in user's claims fresh.
 *
 * Custom claims are baked into the ID token, which Firebase refreshes about
 * once an hour. Without this, a role change, a new membership or a suspension
 * would take up to an hour to take effect and every write in between would be
 * judged against a stale token — a user seeing "permission denied" on something
 * they were just granted, with nothing to tell them to wait.
 *
 * `syncMemberClaims` and `onOrgStatusChange` stamp `/users/{uid}.claimsUpdatedAt`
 * whenever they reissue claims. This watches that field and forces a refresh.
 */
export function watchClaims(
  user: User,
  onClaims: (claims: OrgClaims) => void,
): () => void {
  let lastSeen = 0;

  const stopUserWatch = onSnapshot(doc(db, 'users', user.uid), async (snapshot) => {
    const updatedAt = snapshot.data()?.claimsUpdatedAt;
    const millis: number = updatedAt?.toMillis?.() ?? 0;
    if (millis <= lastSeen) return;
    lastSeen = millis;
    // Force, not the cached token. That is the whole point of this listener.
    await user.getIdToken(true);
  });

  const stopTokenWatch = onIdTokenChanged(auth, async (current) => {
    if (!current) return;
    onClaims(await readClaims(current));
  });

  return () => {
    stopUserWatch();
    stopTokenWatch();
  };
}

export async function readClaims(user: User, forceRefresh = false): Promise<OrgClaims> {
  const result = await user.getIdTokenResult(forceRefresh);
  return {
    orgs: (result.claims.orgs as Record<OrgId, Role>) ?? {},
    susp: (result.claims.susp as OrgId[]) ?? [],
    // The screen only uses this to decide what to render. Every endpoint
    // behind it checks the claim again, server-side, where it is enforced.
    superAdmin: result.claims.superAdmin === true,
  };
}

export const roleIn = (claims: OrgClaims, orgId: OrgId): Role | null =>
  claims.orgs[orgId] ?? null;

export const isSuspended = (claims: OrgClaims, orgId: OrgId): boolean =>
  claims.susp.includes(orgId);

/** Mirrors canWrite() in firestore.rules. The rules remain the enforcement. */
export const canWrite = (claims: OrgClaims, orgId: OrgId): boolean =>
  !!roleIn(claims, orgId) && !isSuspended(claims, orgId);
